"use client";

import { Fragment, useState } from "react";
import Link from "next/link";
import { Check, CircleDollarSign, Minus } from "lucide-react";
import { Section, SectionHeading } from "@/components/platform/v2/Section";
import { PageCta, PageHero } from "@/components/platform/v2/PageEdge";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/platform/v2/ui/accordion";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/platform/v2/ui/tabs";
import { cn } from "@/lib/utils";
import {
  AI_BALANCE,
  AI_LEVELS,
  COMPARE_GROUPS,
  DEMO,
  EXTRA_WORK,
  FAQ,
  HOME_PLANS,
  PERIODS,
  PLANS,
  PRICES,
  PRO_CHANNEL_NOTE,
  STORAGE_KINDS,
  type Period,
  type Plan,
  formatPrice,
} from "@/data/pricing";

const columns = ["BASE", "PRO", "PRO+ 🔥", "GENIUS"];
const extensionGroups = [
  { title: "Клиенты и сообщество", items: ["Цифровая визитка", "Сообщество"], text: "Знакомства, связи и взаимодействие пользователей внутри приложения." },
  { title: "Продажи и механики", items: ["Билеты и события", "Tender — совместная закупка", "Аукцион"], text: "Дополнительные модели продаж, заявок, мероприятий и агрегаторных сценариев." },
  { title: "Контент и цифровые каналы", items: ["Электронная библиотека", "AI-Маскот", "Дополнительный сайт"], text: "Дополнительные способы работы с контентом и клиентскими цифровыми каналами." },
  { title: "Инфраструктура и интеграции", items: ["Фирменная почта", "Интеграция VCENTRE.VOTE"], text: "Инфраструктурные возможности и связь VCENTRE.ONE с другими продуктами экосистемы." },
];
const Cell = ({ value }: { value: string }) =>
  value === "✓" ? (
    <Check className="mx-auto h-4 w-4 text-primary" aria-label="есть" />
  ) : value === "—" ? (
    <Minus className="mx-auto h-4 w-4 text-muted-foreground" aria-label="нет" />
  ) : (
    <span className="block text-sm font-bold">{value}</span>
  );

const PricingPageComponent = () => {
  const [period, setPeriod] = useState<Period>("12");
  const openChat = () =>
    window.dispatchEvent(new CustomEvent("vcentre:open-chat"));
  const price = (plan: Plan) => {
    const current = PRICES[plan.key][period];
    const fallback = PRICES[plan.key]["12"];
    const value = current ?? fallback;
    if (value === null) return <p className="text-xl font-bold">Уточняется</p>;
    return (
      <div>
        <p className="text-3xl font-extrabold">
          {plan.priceFrom && (
            <span className="mr-1 text-base text-muted-foreground">от</span>
          )}
          {formatPrice(value)}
          <span className="text-base text-muted-foreground"> / мес.</span>
        </p>
        <p className="mt-1 text-xs text-muted-foreground">
          {period === "1"
            ? "Оплата за месяц"
            : `Подключение на ${period} месяцев, оплата за весь период`}
        </p>
      </div>
    );
  };
  return (
    <div className="vcentre-public pt-24 sm:pt-28 md:pt-32">
      <PageHero
        eyebrow="Тарифы VCENTRE.ONE"
        title={
          <>
            Одна платформа.
            <br />
            <span className="text-primary">Четыре уровня возможностей.</span>
          </>
        }
        text={
          <p>
            Начните с готовой системы и приложения. Расширяйте клиентские
            каналы, расширения и AI по мере роста бизнеса.
          </p>
        }
        actions={
          <>
            <Link href="/signin?source=pricing" className="btn-primary">
              Подобрать конфигурацию
            </Link>
            <a href="#plans" className="btn-ghost">
              Сравнить тарифы
            </a>
          </>
        }
        visual={
          <img
            src={"/assets/images/platform/pricing-mascot-hero.png"}
            alt="Маскот VCENTRE.IT показывает тарифы BASE, PRO, PRO+ и GENIUS"
            width={1493}
            height={1000}
            loading="eager"
            className="w-full max-w-[330px] animate-float drop-shadow-[0_30px_60px_rgba(0,0,0,0.6)] sm:max-w-[420px]"
          />
        }
      />
      <Section tone="quiet" className="!pb-8 sm:!pb-10">
        <SectionHeading eyebrow="Логика" title="BASE → PRO → PRO+ → GENIUS" />
        <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {HOME_PLANS.map((p, i) => (
            <div key={p.key} className="border-t border-primary/50 py-5">
              <span className="text-xs font-bold text-primary">0{i + 1}</span>
              <h2 className="display-3 mt-3">{p.name}</h2>
              <p className="body-text mt-2">{p.meaning}</p>
            </div>
          ))}
        </div>
      </Section>
      <Section id="plans" className="!pt-8 sm:!pt-10">
        <div className="surface-quiet mx-auto mb-8 max-w-4xl text-center">
          <h2 className="text-lg font-bold text-primary">{DEMO.title}</h2>
          <p className="body-text mt-3">{DEMO.text}</p>
          <Link href="/signin?source=pricing-demo" className="btn-primary mt-5">
            Попробовать в DEMO
          </Link>
        </div>
        <div className="mx-auto flex max-w-md rounded-xl border border-border p-1">
          {PERIODS.map((p) => (
            <button
              key={p.key}
              type="button"
              aria-pressed={period === p.key}
              onClick={() => setPeriod(p.key)}
              className={cn(
                "flex-1 rounded-lg px-3 py-2 text-sm font-bold",
                period === p.key
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground",
              )}
            >
              {p.label}
            </button>
          ))}
        </div>
        <div className="mt-10 grid gap-5 md:grid-cols-2 xl:grid-cols-4">
          {PLANS.map((plan) => (
            <article
              key={plan.key}
              className={cn(
                "surface flex flex-col gap-5",
                plan.key === "proPlus" && "border-primary/50",
              )}
            >
              <div>
                <p className="eyebrow">{plan.ladder}</p>
                <h2 className="display-3 mt-2">
                  {plan.name}
                  {plan.key === "proPlus" && " 🔥"}
                </h2>
                <p className="mt-2 font-bold">{plan.meaning}</p>
              </div>
              {price(plan)}
              {plan.key === "pro" && (
                <div className="flex flex-wrap items-center gap-2 text-sm font-bold">
                  <span className="rounded-lg bg-primary/10 px-2 py-1 text-primary">Приложение</span>
                  <span className="text-muted-foreground">или</span>
                  <span className="rounded-lg bg-primary/10 px-2 py-1 text-primary">Сайт</span>
                </div>
              )}
              <ul className="flex flex-col gap-2">
                {plan.benefits.map((x) => (
                  <li key={x} className="flex gap-2 text-sm text-foreground/85">
                    <Check className="h-4 w-4 shrink-0 text-primary" />
                    {x}
                  </li>
                ))}
              </ul>
              <p className="text-xs text-muted-foreground">AI-баланс: {plan.aiBalance}</p>
              <Link
                href={`/signin?plan=${plan.key === "proPlus" ? "pro-plus" : plan.key}`}
                className="btn-primary mt-auto"
              >
                Подключить {plan.name}
              </Link>
            </article>
          ))}
        </div>
        <div className="mt-8 text-center">
          <Link href="/signin?source=pricing-picker" className="btn-link">
            Подобрать тариф под мой бизнес
          </Link>
        </div>
      </Section>
      <Section tone="quiet">
        <SectionHeading
          eyebrow="Сравнение"
          title="Что меняется между уровнями"
        />
        <div className="mt-10 hidden overflow-hidden rounded-2xl border border-border lg:block">
          <table className="w-full">
            <thead>
              <tr className="bg-[hsl(var(--charcoal))]">
                <th className="p-4 text-left">Возможность</th>
                {columns.map((x) => (
                  <th key={x} className="p-4">
                    {x}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {COMPARE_GROUPS.map((g) => (
                <Fragment key={g.title}>
                  <tr className="bg-[hsl(var(--surface))]/60">
                    <td
                      colSpan={5}
                      className="p-3 text-xs font-bold uppercase text-primary"
                    >
                      {g.title}
                    </td>
                  </tr>
                  {g.rows.map((r) => (
                    <tr key={r.label} className="border-t border-border/40">
                      <td className="p-3 text-sm">{r.label}</td>
                      {r.values.map((v, i) => (
                        <td key={`${r.label}-${i}`} className="p-3 text-center">
                          <Cell value={v} />
                        </td>
                      ))}
                    </tr>
                  ))}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
        <Accordion type="single" collapsible className="mt-8 lg:hidden">
          {COMPARE_GROUPS.map((g) => (
            <AccordionItem key={g.title} value={g.title}>
              <AccordionTrigger>{g.title}</AccordionTrigger>
              <AccordionContent>
                {g.rows.map((r) => (
                  <div key={r.label} className="border-t border-border/50 py-3">
                    <p className="text-sm">{r.label}</p>
                    <div className="mt-2 grid grid-cols-4">
                      {r.values.map((v, i) => (
                        <div
                          key={`${r.label}-m-${i}`}
                          className="text-center text-xs"
                        >
                          <span className="mb-1 block">{columns[i]}</span>
                          <Cell value={v} />
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
        <p className="body-text mx-auto mt-6 max-w-4xl text-center">{PRO_CHANNEL_NOTE}</p>
      </Section>
      <Section>
        <SectionHeading
          eyebrow="Ресурсы и расширения"
          title="Всё дополнительное — в одном месте"
        />
        <Tabs defaultValue="storage" className="mt-10">
          <TabsList className="grid h-auto w-full grid-cols-3">
            <TabsTrigger value="storage">Хранилище</TabsTrigger>
            <TabsTrigger value="ai">AI</TabsTrigger>
            <TabsTrigger value="modules">Расширения</TabsTrigger>
          </TabsList>
          <TabsContent value="storage" className="surface mt-5">
            <div className="grid gap-6 md:grid-cols-2">
              {STORAGE_KINDS.map((x) => (
                <div key={x.title}>
                  <h3 className="font-bold text-primary">{x.title}</h3>
                  <p className="body-text mt-2">{x.text}</p>
                </div>
              ))}
            </div>
            <div className="mt-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
              {PLANS.map((p) => (
                <div key={p.key} className="surface-quiet text-center">
                  <b>{p.name}</b>
                  <p className="mt-1 text-sm text-primary">
                    {p.storage.internal} + {p.storage.external}
                  </p>
                </div>
              ))}
            </div>
          </TabsContent>
          <TabsContent value="ai" className="surface mt-5">
            <p className="lede">
              ONE ежемесячно включает AI-баланс по тарифу. Дополнительное
              пополнение относится к Магазину и общим ресурсам аккаунта VCENTRE.
            </p>
            <div className="mt-6 grid gap-4 md:grid-cols-3">
              {AI_LEVELS.map((x) => (
                <div key={x.title}>
                  <h3 className="font-bold text-primary">{x.title}</h3>
                  <p className="body-text mt-2">{x.text}</p>
                </div>
              ))}
            </div>
            <div className="mt-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
              {AI_BALANCE.map((x) => (
                <div key={x.plan} className="surface-quiet text-center">
                  <b>{x.plan}</b>
                  <p className="text-2xl font-bold text-primary">{x.value}</p>
                  <p className="text-xs text-muted-foreground">в месяц</p>
                </div>
              ))}
            </div>
          </TabsContent>
          <TabsContent value="modules" className="surface mt-5">
            <div className="grid gap-4 md:grid-cols-2">
              {extensionGroups.map((group) => (
                <article key={group.title} className="surface-quiet">
                  <h3 className="font-bold text-primary">{group.title}</h3>
                  <ul className="mt-3 space-y-1 text-sm font-semibold">{group.items.map((item) => <li key={item}>{item}{item === "Дополнительный сайт" && <span className="ml-1 text-xs text-muted-foreground">(PRO+ и GENIUS)</span>}</li>)}</ul>
                  <p className="body-text mt-3">{group.text}</p>
                </article>
              ))}
            </div>
            <div className="mt-6 text-center"><Link href="/platform/modules" className="btn-link">Все расширения</Link></div>
          </TabsContent>
        </Tabs>
      </Section>
      <Section tone="quiet">
        <SectionHeading
          eyebrow="Дополнительные работы"
          title="Дизайн, сценарии и интеграции"
          text="При подключении на 12 месяцев часть работ уже включена."
        />
        <Accordion type="single" collapsible className="mx-auto mt-8 max-w-4xl">
          {EXTRA_WORK.map((x) => (
            <AccordionItem key={x.num} value={x.num}>
              <AccordionTrigger>{x.title}</AccordionTrigger>
              <AccordionContent>
                <p className="body-text mb-4">{x.text}</p>
                <div className="grid gap-3 md:grid-cols-3">
                  {x.tiers.map((t) => (
                    <div key={t.period} className="surface-quiet">
                      <b className="text-primary">{t.period}</b>
                      {t.lines.map((l) => (
                        <p key={l} className="mt-2 text-sm">
                          {l}
                        </p>
                      ))}
                    </div>
                  ))}
                </div>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </Section>
      <Section>
        <SectionHeading eyebrow="FAQ" title="Частые вопросы" />
        <Accordion type="single" collapsible className="mx-auto mt-8 max-w-3xl">
          {FAQ.map((x) => (
            <AccordionItem key={x.q} value={x.q}>
              <AccordionTrigger>{x.q}</AccordionTrigger>
              <AccordionContent className="body-text">{x.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </Section>
      <Section>
        <PageCta
          icon={<CircleDollarSign className="h-6 w-6 text-primary" />}
          title="Соберите конфигурацию под свой процесс"
          text={
            <p>
              Начните с готовой системы и приложения, выберите нужный уровень
              клиентского контура, период подключения и дополнительные возможности.
            </p>
          }
          actions={
            <>
              <Link
                href="/signin?source=pricing-bottom"
                className="btn-primary"
              >
                Собрать конфигурацию
              </Link>
              <button onClick={openChat} className="btn-ghost">
                Задать вопрос
              </button>
            </>
          }
          visual={
            <img
              src={"/assets/images/platform/pricing-mascot-cta.png"}
              alt="Маскот VCENTRE.IT с ракетой — запускаем быстро"
              width={1254}
              height={1254}
              loading="lazy"
              className="w-full max-w-[260px] drop-shadow-[0_24px_48px_rgba(0,0,0,0.55)]"
            />
          }
        />
      </Section>
    </div>
  );
};
export default PricingPageComponent;
